import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@app/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@app/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@app/components/ui/navigation-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@app/components/ui/tooltip';
import { cn } from '@app/lib/utils';
import LoggedInAs from '@cloud-ui/components/LoggedInAs';
import Logo from '@cloud-ui/components/Logo';
import { ROUTES } from '@cloud-ui/constants';
import { useConfig } from '@cloud-ui/contexts/ConfigContext';
import { useCan } from '@cloud-ui/contexts/RbacContext';
import { useLicense } from '@cloud-ui/hooks/useLicense';
import { Actions, Subjects } from '@shared/dto/rbac';
import type { ResolvedTheme, ThemePreference } from '@cloud-ui/hooks/useThemePreference';

import { BarChart3, ChevronDown, Lock, MoreHorizontal } from 'lucide-react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { AuthContext } from '../contexts/AuthContext';
import NewScanOrEvalButton from './NewScanOrEvalButton';
import TeamMenu from './TeamMenu';
import ThemeSelector from './ThemeSelector';

interface NavItem {
  href: string;
  label: string;
  description?: string;
  permission?: { action: Actions; subject: Subjects };
  requiresServerSideJobs?: boolean;
  // Only show in on-prem deployments (hidden when IS_PROMPTFOO_CLOUD is true)
  onPremOnly?: boolean;
  // For guardrails items: which API version this item belongs to
  guardrailsVersion?: 'v1' | 'v2';
  // For eval creator items: requires feature flag to be enabled
  requiresEvalCreator?: boolean;
  // Test ID for Playwright testing
  testId?: string;
}

interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
  permission?: { action: Actions; subject: Subjects };
}

type NavigationEntry =
  | {
      id: string;
      type: 'section';
      section: NavSection;
    }
  | {
      id: string;
      type: 'link';
      item: NavItem;
    };

const NAV_ITEM_GAP_PX = 4;
const OVERFLOW_TRIGGER_FALLBACK_WIDTH = 40;
const NAV_OVERFLOW_MENU_ATTR = 'data-nav-overflow-menu';
const OVERFLOW_MENU_CLOSE_DELAY_MS = 120;

// Static definition of all possible navigation sections and items
// Note: organizationOverviewHref is dynamic and will be handled in the useMemo
const ALL_NAV_SECTIONS: NavSection[] = [
  {
    id: 'redteam',
    label: 'Red Team',
    items: [
      {
        href: ROUTES.redteam.targets,
        label: 'Targets',
        description: 'LLM applications, models, and endpoints to test',
        permission: { action: Actions.READ, subject: Subjects.PROVIDER },
      },
      {
        href: ROUTES.redteam.teamProvider,
        label: 'Team Inference',
        description: "Configure this team's inference providers",
        permission: { action: Actions.READ, subject: Subjects.PROVIDER },
        onPremOnly: true,
      },
      {
        href: ROUTES.redteam.configs,
        label: 'Scan Templates',
        description: 'Saved attack profiles for testing',
        permission: { action: Actions.READ, subject: Subjects.CONFIG },
      },
      {
        href: ROUTES.redteam.report,
        label: 'Reports',
        description: 'Security assessment summaries',
        permission: { action: Actions.READ, subject: Subjects.EVAL },
      },
      {
        href: ROUTES.redteam.vulnerabilities,
        label: 'Vulnerabilities',
        description: 'Track discovered security issues',
        permission: { action: Actions.READ, subject: Subjects.ISSUE },
      },
      {
        href: ROUTES.jobs,
        label: 'Scan History',
        description: 'View past security scan results',
        permission: { action: Actions.READ, subject: Subjects.JOB },
        requiresServerSideJobs: true,
        onPremOnly: true,
      },
      {
        href: ROUTES.redteam.plugins,
        label: 'Plugins',
        description: 'Manage security tests for specific vulnerabilities',
        permission: { action: Actions.READ, subject: Subjects.PLUGIN_COLLECTION },
      },
      {
        href: ROUTES.redteam.customPolicies,
        label: 'Policy Library',
        description: 'Define guidelines to test against',
        permission: { action: Actions.READ, subject: Subjects.PLUGIN_COLLECTION },
      },
      {
        href: ROUTES.redteam.externalGuidelines,
        label: 'Grading Guidelines',
        description: 'Upload documents to customize how results are graded',
        permission: { action: Actions.READ, subject: Subjects.GRADING_GUIDELINE },
        testId: 'nav-external-guidelines',
      },
    ],
  },
  {
    id: 'evals',
    label: 'Evals',
    items: [
      {
        href: ROUTES.evals,
        label: 'Eval Results',
        description: 'Eval and Red Team Results',
        permission: { action: Actions.READ, subject: Subjects.EVAL },
      },
      {
        href: ROUTES.evalResultTags,
        label: 'Result Tags',
        description: 'Manage review tags for eval results',
        permission: { action: Actions.READ, subject: Subjects.EVAL },
        testId: 'nav-result-tags',
      },
      {
        href: ROUTES.providers,
        label: 'Providers',
        description: 'LLM applications, models, and endpoints to test',
        permission: { action: Actions.READ, subject: Subjects.PROVIDER },
        testId: 'nav-providers',
        requiresEvalCreator: true,
      },
      {
        href: ROUTES.evalConfigs,
        label: 'Eval Configs',
        description: 'Manage saved eval configurations',
        permission: { action: Actions.READ, subject: Subjects.EVAL },
        requiresEvalCreator: true,
        testId: 'nav-eval-configs',
      },
      {
        href: ROUTES.datasets,
        label: 'Datasets',
        description: 'Manage test datasets',
        permission: { action: Actions.READ, subject: Subjects.EVAL },
        requiresEvalCreator: true,
        testId: 'nav-datasets',
      },
      {
        href: ROUTES.prompts,
        label: 'Prompts',
        description: 'Browse prompts used across evaluations',
        permission: { action: Actions.READ, subject: Subjects.EVAL },
        requiresEvalCreator: true,
        testId: 'nav-prompts',
      },
    ],
  },
  {
    id: 'reports',
    label: 'Dashboards',
    items: [
      {
        href: ROUTES.reports.teamActivity,
        label: 'Team Activity',
        description: 'Scan volume across targets',
        permission: { action: Actions.READ, subject: Subjects.REPORT },
      },
      {
        href: ROUTES.reports.vulnerabilityPosture,
        label: 'Vulnerability Posture',
        description: 'Open vulns, remediation trends, and severity mix',
        permission: { action: Actions.READ, subject: Subjects.REPORT },
      },
      // Executive Summary href is dynamic (organization.id), handled in useMemo
    ],
  },
  {
    id: 'guardrails',
    label: 'Guardrails',
    items: [
      {
        href: ROUTES.guardrails.index,
        label: 'Dashboard',
        description: 'Overview of guardrail performance and policies',
        guardrailsVersion: 'v1',
      },
      {
        href: ROUTES.guardrails.dashboard,
        label: 'Dashboard',
        description: 'Monitor guardrails performance metrics and trends',
        guardrailsVersion: 'v2',
      },
      {
        href: ROUTES.guardrails.targets,
        label: 'Targets',
        description: 'View and manage targets protected by guardrails',
        guardrailsVersion: 'v2',
      },
      {
        href: ROUTES.guardrails.list,
        label: 'Guardrails',
        description: 'Manage guardrails and their policies',
        guardrailsVersion: 'v2',
      },
      {
        href: ROUTES.guardrails.requests,
        label: 'Requests',
        description: 'View historical guardrail evaluation requests',
        guardrailsVersion: 'v2',
      },
    ],
  },
];

function NavLink({
  href,
  label,
  onClick,
  onMouseEnter,
  disabled = false,
}: {
  href: string;
  label: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  disabled?: boolean;
}) {
  const location = useLocation();
  const isActive = location.pathname === href;

  if (disabled) {
    return (
      <Button type="button" variant="ghost" disabled className="min-w-0 text-muted-foreground">
        {label}
        <Lock className="size-4" />
      </Button>
    );
  }

  return (
    <RouterLink
      to={href}
      aria-current={isActive ? 'page' : undefined}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={cn(
        'inline-flex h-8 items-center justify-center rounded-md px-3 text-sm font-medium no-underline transition-colors hover:no-underline',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isActive
          ? 'bg-primary/10 text-primary hover:bg-primary/15 focus-visible:bg-primary/15'
          : 'text-foreground hover:bg-accent focus-visible:bg-accent',
      )}
    >
      {label}
    </RouterLink>
  );
}

function isPathMatch(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function getActiveNavItemHref(items: Pick<NavItem, 'href'>[], pathname: string) {
  return (
    items
      .filter((item) => isPathMatch(pathname, item.href))
      .sort((a, b) => b.href.length - a.href.length)[0]?.href ?? null
  );
}

function getNavigationEntryLabel(entry: NavigationEntry) {
  return entry.type === 'section' ? entry.section.label : entry.item.label;
}

function isNavigationEntryActive(entry: NavigationEntry, pathname: string) {
  if (entry.type === 'section') {
    return getActiveNavItemHref(entry.section.items, pathname) !== null;
  }

  return isPathMatch(pathname, entry.item.href);
}

function getMeasuredWidth(element: HTMLElement | null | undefined) {
  return element?.getBoundingClientRect().width ?? 0;
}

function getNavItemsWidth(widths: number[], itemCount: number) {
  const itemWidths = widths.slice(0, itemCount).reduce((total, width) => total + width, 0);
  const gapWidth = Math.max(0, itemCount - 1) * NAV_ITEM_GAP_PX;

  return itemWidths + gapWidth;
}

function isOverflowNavigationElement(target: EventTarget | null) {
  return target instanceof Element && target.closest(`[${NAV_OVERFLOW_MENU_ATTR}="true"]`) !== null;
}

function useNavigationOverflow(entries: NavigationEntry[]) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rightActionsRef = useRef<HTMLDivElement>(null);
  const overflowMeasureRef = useRef<HTMLDivElement>(null);
  const measurementRefs = useRef(new Map<string, HTMLDivElement>());
  const [visibleCount, setVisibleCount] = useState(entries.length);

  const setMeasurementRef = useCallback((entryId: string) => {
    return (node: HTMLDivElement | null) => {
      if (node) {
        measurementRefs.current.set(entryId, node);
      } else {
        measurementRefs.current.delete(entryId);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') {
      setVisibleCount(entries.length);
      return;
    }

    const requestFrame = window.requestAnimationFrame ?? window.setTimeout;
    const cancelFrame = window.cancelAnimationFrame ?? window.clearTimeout;
    let rafId: number | null = null;

    const measure = () => {
      const containerWidth = getMeasuredWidth(containerRef.current);
      const entryWidths = entries.map((entry) =>
        getMeasuredWidth(measurementRefs.current.get(entry.id)),
      );

      if (!containerRef.current || entryWidths.some((width) => width <= 0)) {
        setVisibleCount(entries.length);
        return;
      }

      if (containerWidth <= 0) {
        setVisibleCount(0);
        return;
      }

      if (getNavItemsWidth(entryWidths, entries.length) <= containerWidth) {
        setVisibleCount(entries.length);
        return;
      }

      const overflowWidth =
        getMeasuredWidth(overflowMeasureRef.current) || OVERFLOW_TRIGGER_FALLBACK_WIDTH;
      let nextVisibleCount = entries.length - 1;

      while (nextVisibleCount > 0) {
        const visibleItemsWidth = getNavItemsWidth(entryWidths, nextVisibleCount);
        const overflowGapWidth = nextVisibleCount > 0 ? NAV_ITEM_GAP_PX : 0;

        if (visibleItemsWidth + overflowGapWidth + overflowWidth <= containerWidth) {
          break;
        }

        nextVisibleCount -= 1;
      }

      setVisibleCount(nextVisibleCount);
    };

    const scheduleMeasure = () => {
      if (rafId !== null) {
        cancelFrame(rafId);
      }

      rafId = requestFrame(() => {
        rafId = null;
        measure();
      });
    };

    scheduleMeasure();

    const resizeObserver = new ResizeObserver(scheduleMeasure);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    if (rightActionsRef.current) {
      resizeObserver.observe(rightActionsRef.current);
    }
    if (overflowMeasureRef.current) {
      resizeObserver.observe(overflowMeasureRef.current);
    }
    measurementRefs.current.forEach((node) => resizeObserver.observe(node));
    window.addEventListener('resize', scheduleMeasure);

    return () => {
      if (rafId !== null) {
        cancelFrame(rafId);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [entries]);

  return {
    containerRef,
    rightActionsRef,
    overflowMeasureRef,
    setMeasurementRef,
    visibleCount,
  };
}

interface NavigationProps {
  themePreference: ThemePreference;
  systemTheme: ResolvedTheme;
  onThemePreferenceChange: (preference: ThemePreference) => void;
}

function NavSectionDropdown({
  section,
  onMouseEnter,
}: {
  section: NavSection;
  onMouseEnter?: () => void;
}) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<number | null>(null);
  const triggerId = `${section.id}-button`;
  const menuId = `${section.id}-menu`;

  // Use the most specific matching item so /evals does not also mark /evals/result-tags active.
  const activeItemHref = useMemo(
    () => getActiveNavItemHref(section.items, location.pathname),
    [section.items, location.pathname],
  );
  const isChildActive = activeItemHref !== null;

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(true);
  }, [clearCloseTimeout]);

  const scheduleCloseMenu = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null;
      setIsOpen(false);
    }, OVERFLOW_MENU_CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);

  const handleTriggerMouseEnter = useCallback(() => {
    onMouseEnter?.();
    openMenu();
  }, [onMouseEnter, openMenu]);

  const handleTriggerPointerDown = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  const handleTriggerClick = useCallback(() => {
    openMenu();
  }, [openMenu]);

  const closeWhenLeavingRelatedElement = useCallback(
    (event: ReactMouseEvent<HTMLElement>, relatedElementId: string) => {
      const relatedTarget = event.relatedTarget;
      if (!(relatedTarget instanceof Element) || !relatedTarget.closest(`#${relatedElementId}`)) {
        scheduleCloseMenu();
      }
    },
    [scheduleCloseMenu],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      clearCloseTimeout();
      setIsOpen(open);
    },
    [clearCloseTimeout],
  );

  useEffect(() => clearCloseTimeout, [clearCloseTimeout]);

  return (
    <NavigationMenuItem>
      <DropdownMenu modal={false} open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            id={triggerId}
            type="button"
            onMouseEnter={handleTriggerMouseEnter}
            onMouseLeave={(event) => closeWhenLeavingRelatedElement(event, menuId)}
            onPointerDown={handleTriggerPointerDown}
            onClick={handleTriggerClick}
            className={cn(
              'inline-flex h-8 items-center justify-center rounded-md bg-transparent px-3 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              isOpen && 'bg-accent',
              isChildActive
                ? 'bg-primary/10 text-primary hover:bg-primary/15 focus-visible:bg-primary/15'
                : 'text-foreground hover:bg-accent focus-visible:bg-accent',
            )}
          >
            {section.label}
            <ChevronDown
              aria-hidden="true"
              className={cn('ml-1 size-3 transition duration-300', isOpen && 'rotate-180')}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          id={menuId}
          align="start"
          sideOffset={6}
          onMouseEnter={openMenu}
          onMouseLeave={(event) => closeWhenLeavingRelatedElement(event, triggerId)}
          className="w-[360px] rounded-lg bg-card p-1.5 text-card-foreground shadow-lg ring-1 ring-black/5 dark:ring-white/10"
        >
          {section.items.map((item, index) => {
            const isItemActive = activeItemHref === item.href;
            return (
              <DropdownMenuItem
                key={item.href}
                asChild
                className={cn(
                  'h-auto rounded-lg px-3 py-2.5 text-foreground hover:bg-accent focus:bg-accent',
                  index !== section.items.length - 1 && 'mb-0.5',
                  isItemActive &&
                    'bg-primary/10 text-primary hover:bg-primary/15 focus:bg-primary/15',
                )}
              >
                <RouterLink
                  to={item.href}
                  aria-current={isItemActive ? 'page' : undefined}
                  data-testid={item.testId}
                  className="block w-full select-none no-underline outline-none hover:no-underline"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="text-sm font-medium">{item.label}</span>
                    {item.description && (
                      <span className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                        {item.description}
                      </span>
                    )}
                  </span>
                </RouterLink>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </NavigationMenuItem>
  );
}

function NavigationEntryItem({
  entry,
  onMouseEnter,
}: {
  entry: NavigationEntry;
  onMouseEnter?: () => void;
}) {
  if (entry.type === 'section') {
    return <NavSectionDropdown section={entry.section} onMouseEnter={onMouseEnter} />;
  }

  return (
    <NavigationMenuItem>
      <NavigationMenuLink asChild>
        <NavLink href={entry.item.href} label={entry.item.label} onMouseEnter={onMouseEnter} />
      </NavigationMenuLink>
    </NavigationMenuItem>
  );
}

function NavigationMeasurements({
  entries,
  overflowMeasureRef,
  setMeasurementRef,
}: {
  entries: NavigationEntry[];
  overflowMeasureRef: RefObject<HTMLDivElement | null>;
  setMeasurementRef: (entryId: string) => (node: HTMLDivElement | null) => void;
}) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none invisible absolute left-0 top-0 flex gap-1 whitespace-nowrap"
    >
      {entries.map((entry) => (
        <div
          key={entry.id}
          ref={setMeasurementRef(entry.id)}
          data-nav-measure-id={entry.id}
          className="inline-flex h-8 items-center justify-center rounded-md px-3 text-sm font-medium"
        >
          {getNavigationEntryLabel(entry)}
          {entry.type === 'section' && <span className="ml-1 inline-block size-3" />}
        </div>
      ))}
      <div
        ref={overflowMeasureRef}
        data-nav-overflow-measure="true"
        className="inline-flex size-8 items-center justify-center rounded-md"
      >
        <MoreHorizontal className="size-5" />
      </div>
    </div>
  );
}

function OverflowNavigationItem({ item }: { item: NavItem }) {
  const location = useLocation();
  const isActive = isPathMatch(location.pathname, item.href);

  return (
    <DropdownMenuItem
      asChild
      className={cn(
        'h-auto rounded-lg px-3 py-2.5 text-sm font-medium text-foreground hover:bg-accent focus:bg-accent',
        isActive && 'bg-primary/10 text-primary hover:bg-primary/15 focus:bg-primary/15',
      )}
    >
      <RouterLink
        to={item.href}
        aria-current={isActive ? 'page' : undefined}
        data-testid={item.testId}
        className="no-underline hover:no-underline"
      >
        {item.label}
      </RouterLink>
    </DropdownMenuItem>
  );
}

interface OverflowNavigationSectionProps {
  section: NavSection;
  onMouseEnter: () => void;
  onMouseOver: () => void;
  onMouseLeave: (event: ReactMouseEvent<HTMLElement>) => void;
  onPointerEnter: () => void;
  onPointerOver: () => void;
  onPointerLeave: (event: ReactPointerEvent<HTMLElement>) => void;
}

function OverflowNavigationSection({
  section,
  onMouseEnter,
  onMouseOver,
  onMouseLeave,
  onPointerEnter,
  onPointerOver,
  onPointerLeave,
}: OverflowNavigationSectionProps) {
  const location = useLocation();
  const activeItemHref = useMemo(
    () => getActiveNavItemHref(section.items, location.pathname),
    [section.items, location.pathname],
  );

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        data-testid={`nav-overflow-section-${section.id}`}
        data-nav-overflow-menu="true"
        onMouseOver={onMouseOver}
        onPointerEnter={onPointerEnter}
        onPointerOver={onPointerOver}
        className={cn(
          'h-auto rounded-lg px-3 py-2.5 text-sm font-medium text-foreground hover:bg-accent focus:bg-accent data-[state=open]:bg-accent [&>svg]:mt-0.5',
          activeItemHref &&
            'bg-primary/10 text-primary hover:bg-primary/15 focus:bg-primary/15 data-[state=open]:bg-primary/10',
        )}
      >
        {section.label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent
        data-nav-overflow-menu="true"
        onMouseEnter={onMouseEnter}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
        onPointerEnter={onPointerEnter}
        onPointerOver={onPointerOver}
        onPointerLeave={onPointerLeave}
        className="flex w-[360px] flex-col gap-1 rounded-lg bg-card p-1.5 text-card-foreground shadow-lg ring-1 ring-black/5 dark:ring-white/10"
      >
        {section.items.map((item) => (
          <DropdownMenuItem
            key={item.href}
            asChild
            className={cn(
              'h-auto rounded-lg px-3 py-2.5 text-foreground hover:bg-accent focus:bg-accent',
              activeItemHref === item.href &&
                'bg-primary/10 text-primary hover:bg-primary/15 focus:bg-primary/15',
            )}
          >
            <RouterLink
              to={item.href}
              aria-current={activeItemHref === item.href ? 'page' : undefined}
              data-testid={item.testId}
              className="no-underline hover:no-underline"
            >
              <span className="flex min-w-0 flex-col">
                <span className="font-medium">{item.label}</span>
                {item.description && (
                  <span
                    className={cn(
                      'mt-0.5 text-xs leading-relaxed',
                      activeItemHref === item.href ? 'text-primary/80' : 'text-muted-foreground',
                    )}
                  >
                    {item.description}
                  </span>
                )}
              </span>
            </RouterLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

function OverflowNavigationMenu({
  entries,
  isOpen,
  setIsOpen,
}: {
  entries: NavigationEntry[];
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}) {
  const location = useLocation();
  const isActive = entries.some((entry) => isNavigationEntryActive(entry, location.pathname));
  const overflowMenuContentId = useId().replaceAll(':', '');
  const closeTimeoutRef = useRef<number | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const openOverflowMenu = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(true);
  }, [clearCloseTimeout, setIsOpen]);

  const scheduleCloseOverflowMenu = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null;
      setIsOpen(false);
    }, OVERFLOW_MENU_CLOSE_DELAY_MS);
  }, [clearCloseTimeout, setIsOpen]);

  const handleOverflowMouseLeave = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (isOverflowNavigationElement(event.relatedTarget)) {
        return;
      }

      scheduleCloseOverflowMenu();
    },
    [scheduleCloseOverflowMenu],
  );

  const handleOverflowPointerLeave = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (isOverflowNavigationElement(event.relatedTarget)) {
        return;
      }

      scheduleCloseOverflowMenu();
    },
    [scheduleCloseOverflowMenu],
  );

  const handleDocumentHover = useCallback(
    (event: PointerEvent | MouseEvent) => {
      if (isOverflowNavigationElement(event.target)) {
        clearCloseTimeout();
        return;
      }

      scheduleCloseOverflowMenu();
    },
    [clearCloseTimeout, scheduleCloseOverflowMenu],
  );

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return;
    }

    document.addEventListener('pointerover', handleDocumentHover);
    document.addEventListener('pointermove', handleDocumentHover);
    document.addEventListener('mouseover', handleDocumentHover);
    document.addEventListener('mousemove', handleDocumentHover);

    return () => {
      document.removeEventListener('pointerover', handleDocumentHover);
      document.removeEventListener('pointermove', handleDocumentHover);
      document.removeEventListener('mouseover', handleDocumentHover);
      document.removeEventListener('mousemove', handleDocumentHover);
    };
  }, [handleDocumentHover, isOpen]);

  useEffect(() => {
    return clearCloseTimeout;
  }, [clearCloseTimeout]);

  return (
    <DropdownMenu modal={false} open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="More navigation"
          aria-controls={isOpen ? overflowMenuContentId : undefined}
          data-testid="nav-overflow-menu-button"
          data-nav-overflow-menu="true"
          onMouseEnter={openOverflowMenu}
          onMouseMove={openOverflowMenu}
          onMouseOver={openOverflowMenu}
          onMouseLeave={handleOverflowMouseLeave}
          onPointerEnter={openOverflowMenu}
          onPointerMove={openOverflowMenu}
          onPointerOver={openOverflowMenu}
          onPointerLeave={handleOverflowPointerLeave}
          className={cn(
            'size-8 text-foreground hover:bg-accent focus-visible:bg-accent',
            isActive &&
              'bg-primary/10 text-primary hover:bg-primary/15 focus-visible:bg-primary/15',
          )}
        >
          <MoreHorizontal className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        id={overflowMenuContentId}
        data-testid="nav-overflow-menu"
        data-nav-overflow-menu="true"
        align="start"
        onMouseEnter={openOverflowMenu}
        onMouseOver={openOverflowMenu}
        onMouseLeave={handleOverflowMouseLeave}
        onPointerEnter={openOverflowMenu}
        onPointerOver={openOverflowMenu}
        onPointerLeave={handleOverflowPointerLeave}
        className="flex w-64 flex-col gap-1 rounded-lg bg-card p-1.5 text-card-foreground shadow-lg ring-1 ring-black/5 dark:ring-white/10"
      >
        {entries.map((entry) =>
          entry.type === 'section' ? (
            <OverflowNavigationSection
              key={entry.id}
              section={entry.section}
              onMouseEnter={openOverflowMenu}
              onMouseOver={openOverflowMenu}
              onMouseLeave={handleOverflowMouseLeave}
              onPointerEnter={openOverflowMenu}
              onPointerOver={openOverflowMenu}
              onPointerLeave={handleOverflowPointerLeave}
            />
          ) : (
            <OverflowNavigationItem key={entry.id} item={entry.item} />
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Navigation({ themePreference, systemTheme, onThemePreferenceChange }: NavigationProps) {
  const { enableServerSideJobs, evalCreatorEnabled, isPromptfooCloud, version } = useConfig();
  const { user, organization, isAdmin } = useContext(AuthContext) || {};
  const canViewEvals = useCan(Actions.READ, Subjects.EVAL);
  const canViewIssues = useCan(Actions.READ, Subjects.ISSUE);
  const canViewTargets = useCan(Actions.READ, Subjects.PROVIDER);
  const canViewPlugins = useCan(Actions.READ, Subjects.PLUGIN_COLLECTION);
  const canViewConfigs = useCan(Actions.READ, Subjects.CONFIG);
  const canViewJobs = useCan(Actions.READ, Subjects.JOB);
  const canViewReports = useCan(Actions.READ, Subjects.REPORT);
  const canViewGradingGuidelines = useCan(Actions.READ, Subjects.GRADING_GUIDELINE);
  const canCreateJobs = useCan(Actions.CREATE, Subjects.JOB);

  const { licenseInfo } = useLicense();
  const isRedTeamCustomer = Boolean(organization?.canUseRedteam && !licenseInfo.isCutOff);
  const isPureEvalsCustomer = !isRedTeamCustomer;

  const organizationOverviewHref = organization?.id
    ? ROUTES.organization.overview.replace(':orgId', organization.id)
    : ROUTES.organization.overview;

  // Create a permission map for easy lookup
  const permissionMap: Partial<Record<Subjects, boolean>> = useMemo(
    () => ({
      [Subjects.EVAL]: canViewEvals,
      [Subjects.ISSUE]: canViewIssues,
      [Subjects.PROVIDER]: canViewTargets,
      [Subjects.CONFIG]: canViewConfigs,
      [Subjects.PLUGIN_COLLECTION]: canViewPlugins,
      [Subjects.JOB]: canViewJobs,
      [Subjects.REPORT]: canViewReports,
      [Subjects.GRADING_GUIDELINE]: canViewGradingGuidelines,
    }),
    [
      canViewEvals,
      canViewIssues,
      canViewTargets,
      canViewConfigs,
      canViewPlugins,
      canViewJobs,
      canViewReports,
      canViewGradingGuidelines,
    ],
  );

  // Filter nav sections based on permissions
  const navSections: NavSection[] = useMemo(() => {
    return ALL_NAV_SECTIONS.map((section) => {
      // Check section-level permission
      if (section.permission) {
        const hasPermission = permissionMap[section.permission.subject] ?? false;
        if (!hasPermission) {
          return { ...section, items: [] };
        }
      }

      // Filter items based on permissions
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Baseline cognitive complexity as of 2026-04-27; preserve behavior until refactor.
      let items = section.items.filter((item) => {
        // Check permission if specified
        if (item.permission) {
          const hasPermission = permissionMap[item.permission.subject] ?? false;
          if (!hasPermission) {
            return false;
          }
        }

        // Check server-side jobs requirement
        if (item.requiresServerSideJobs && !enableServerSideJobs) {
          return false;
        }

        // Hide on-prem-only items in cloud deployments
        if (item.onPremOnly && isPromptfooCloud) {
          return false;
        }

        // Filter guardrails items based on combined canUseAdaptiveGuardrails + guardrailsMode
        // This maintains backward compatibility with existing customers
        if (item.guardrailsVersion) {
          const canUse = organization?.canUseAdaptiveGuardrails;
          const mode = organization?.guardrailsMode;

          if (item.guardrailsVersion === 'v1') {
            // v1 items (Dashboard): visible if:
            // - guardrailsMode = 'v1' explicitly set, OR
            // - canUseAdaptiveGuardrails = true AND mode is NOT 'v2' (backward compat fallback)
            // This ensures v2 mode shows ONLY v2 UI, not both
            return mode === 'v1' || (canUse === true && mode !== 'v2');
          } else if (item.guardrailsVersion === 'v2') {
            // v2 items (Dashboard, Guardrails, Requests): visible if BOTH conditions are met
            // canUseAdaptiveGuardrails = true AND guardrailsMode = 'v2'
            return canUse === true && mode === 'v2';
          }
          return false;
        }

        // Filter eval creator items based on feature flag
        if (item.requiresEvalCreator && !evalCreatorEnabled) {
          return false;
        }

        return true;
      });

      // Add dynamic Executive Summary item to Reports section
      if (section.id === 'reports' && organization?.canUseRedteam && canViewIssues) {
        items = [
          ...items,
          {
            href: organizationOverviewHref,
            label: 'Executive Summary',
            description: 'High-level scan performance snapshot',
            testId: 'nav-organization-overview',
          },
        ];
      }

      return { ...section, items };
    }).filter((section) => section.items.length > 0);
  }, [
    permissionMap,
    enableServerSideJobs,
    evalCreatorEnabled,
    isPromptfooCloud,
    organizationOverviewHref,
    organization?.canUseAdaptiveGuardrails,
    organization?.canUseRedteam,
    organization?.guardrailsMode,
    canViewIssues,
  ]);

  const standaloneNavItems: NavItem[] = useMemo(
    () => [...(canViewEvals ? [{ href: ROUTES.modelAudit, label: 'Model Audit' }] : [])],
    [canViewEvals],
  );

  const jobsNavSection: NavSection | null = useMemo(() => {
    if (!canViewJobs || !enableServerSideJobs || isPromptfooCloud) {
      return null;
    }

    return {
      id: 'jobs',
      label: 'Jobs',
      items: [
        {
          href: ROUTES.jobs,
          label: 'My Team Jobs',
          description: 'View jobs in the active team',
        },
        ...(isAdmin
          ? [
              {
                href: ROUTES.adminJobs,
                label: 'Admin Jobs',
                description: 'View jobs across all teams',
              },
            ]
          : []),
      ],
    };
  }, [canViewJobs, enableServerSideJobs, isAdmin, isPromptfooCloud]);

  // Filter sections based on customer type and permissions
  const visibleSections = useMemo(
    () =>
      navSections.filter((section) => {
        if (section.id === 'redteam' && isPureEvalsCustomer) {
          return false; // Hide red team section for pure evals customers
        }
        // Guardrails section visibility is handled by the item filter above, which considers both
        // canUseAdaptiveGuardrails and guardrailsMode.
        // If no items pass the filter, section.items.length will be 0 and section hidden
        return section.items.length > 0;
      }),
    [isPureEvalsCustomer, navSections],
  );

  const navigationEntries = useMemo<NavigationEntry[]>(
    () => [
      ...visibleSections.map(
        (section) =>
          ({
            id: `section:${section.id}`,
            type: 'section',
            section,
          }) satisfies NavigationEntry,
      ),
      ...(jobsNavSection
        ? [
            {
              id: `section:${jobsNavSection.id}`,
              type: 'section',
              section: jobsNavSection,
            } satisfies NavigationEntry,
          ]
        : []),
      ...standaloneNavItems.map(
        (item) =>
          ({
            id: `link:${item.href}`,
            type: 'link',
            item,
          }) satisfies NavigationEntry,
      ),
    ],
    [jobsNavSection, standaloneNavItems, visibleSections],
  );

  const { containerRef, rightActionsRef, overflowMeasureRef, setMeasurementRef, visibleCount } =
    useNavigationOverflow(navigationEntries);

  const visibleNavigationEntries = user ? navigationEntries.slice(0, visibleCount) : [];
  const overflowNavigationEntries = user ? navigationEntries.slice(visibleCount) : [];
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const closeOverflowMenu = useCallback(() => setIsOverflowMenuOpen(false), []);
  const closeOverflowMenuForTarget = useCallback((target: EventTarget | null) => {
    if (target instanceof Element && !isOverflowNavigationElement(target)) {
      setIsOverflowMenuOpen(false);
    }
  }, []);

  const handleNavigationPointerOver = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      closeOverflowMenuForTarget(event.target);
    },
    [closeOverflowMenuForTarget],
  );

  const handleNavigationMouseOver = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      closeOverflowMenuForTarget(event.target);
    },
    [closeOverflowMenuForTarget],
  );
  const handleNavigationMenuValueChange = useCallback((value: string) => {
    if (value) {
      setIsOverflowMenuOpen(false);
    }
  }, []);

  const navigationMenu = user && (
    <div
      ref={containerRef}
      data-nav-overflow-container="true"
      data-testid="primary-navigation"
      className="relative min-w-8 flex-1"
    >
      <NavigationMenu
        className="max-w-full justify-start"
        onValueChange={handleNavigationMenuValueChange}
      >
        <NavigationMenuList
          className="justify-start gap-1"
          onMouseOverCapture={handleNavigationMouseOver}
          onPointerOverCapture={handleNavigationPointerOver}
        >
          {visibleNavigationEntries.map((entry) => (
            <NavigationEntryItem key={entry.id} entry={entry} onMouseEnter={closeOverflowMenu} />
          ))}
          {overflowNavigationEntries.length > 0 && (
            <NavigationMenuItem>
              <OverflowNavigationMenu
                entries={overflowNavigationEntries}
                isOpen={isOverflowMenuOpen}
                setIsOpen={setIsOverflowMenuOpen}
              />
            </NavigationMenuItem>
          )}
        </NavigationMenuList>
      </NavigationMenu>
      <NavigationMeasurements
        entries={navigationEntries}
        overflowMeasureRef={overflowMeasureRef}
        setMeasurementRef={setMeasurementRef}
      />
    </div>
  );

  return (
    <header
      data-testid="app-navigation"
      className="fixed left-0 right-0 top-0 z-(--z-appbar) flex h-[66px] items-center border-b border-border bg-card text-foreground shadow-sm"
    >
      <div className="flex h-full w-full items-center justify-between px-2 sm:px-4">
        <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 sm:gap-4 md:gap-6">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex shrink-0">
                <Logo compactOnMobile />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {version ? `Version ${version}` : 'Version unavailable'}
            </TooltipContent>
          </Tooltip>
          {user && (
            <div className="min-w-0 max-w-[300px] shrink-0">
              <TeamMenu />
            </div>
          )}
          {navigationMenu}
        </div>
        <div
          ref={rightActionsRef}
          data-nav-right-actions="true"
          data-testid="navigation-actions"
          className="flex shrink-0 items-center gap-1"
        >
          {user && (
            <NewScanOrEvalButton
              canCreateJobs={canCreateJobs}
              evalCreatorEnabled={evalCreatorEnabled ?? false}
              isRedTeamEnabled={isRedTeamCustomer}
            />
          )}
          {organization?.canUseRedteam && canViewIssues && (
            <Button
              asChild
              variant="ghost"
              size="icon"
              aria-label="Organization overview"
              className="hidden sm:inline-flex"
            >
              <RouterLink to={ROUTES.organization.overview.replace(':orgId', organization!.id!)}>
                <BarChart3 className="size-5" />
              </RouterLink>
            </Button>
          )}
          <ThemeSelector
            themePreference={themePreference}
            systemTheme={systemTheme}
            onThemePreferenceChange={onThemePreferenceChange}
          />
          <LoggedInAs />
        </div>
      </div>
    </header>
  );
}

export default Navigation;
